import React from 'react';
import { hasPublishedCustomerComment } from '../../utils/commentHelpers';

/**
 * NewFeedbackBanner - Estimat-specifik banner för nya osparade kommentarer
 * Visas när kunden redan har skickat feedback en gång och har nya radkommentarer
 * att skicka.
 * 
 * OBS: Denna är INTE generisk - den hanterar radkommentarer som är specifika för estimat.
 * 
 * @param {Object} estimatData - Estimatdata från servern
 * @param {Object} savedComments - Lokalt sparade kommentarer
 * @param {boolean} isCustomerView - Om användaren är kund
 * @param {boolean} submitting - Om formuläret håller på att skickas
 * @param {function} onSendFeedback - Callback när användaren klickar "Skicka ny feedback"
 */
const NewFeedbackBanner = ({
  estimatData,
  savedComments,
  isCustomerView,
  submitting,
  onSendFeedback,
}) => {
  // Kontrollera om feedback VERKLIGEN har skickats (publicerade kommentarer eller BK-svar finns)
  // Per-rad draft-flagga är källan till sanning — autosparade utkast räknas inte
  const existingCommentsAll = estimatData?.customerResponse?.comments || {};
  const hasPublishedComments = Object.values(existingCommentsAll).some(hasPublishedCustomerComment);
  const hasAnyFeedbackOnEstimat = Boolean(
    hasPublishedComments ||
    (estimatData?.byggkonsultResponses && Object.keys(estimatData.byggkonsultResponses).length > 0)
  );
  
  // Hitta kommentarer som är nya:
  // 1. Lokalt sparade men inte i serverdata (savedComments)
  // 2. Draft-kommentarer i DB som inte publicerats
  const existingComments = estimatData?.customerResponse?.comments || {};

  // Lokalt sparade kommentarer som inte matchar servern
  const localNewComments = Object.entries(savedComments).filter(([key, value]) => {
    if (!value?.trim()) return false;
    const existingComment = existingComments[key];
    const existingText = typeof existingComment === 'object' ? (existingComment?.text || '') : (existingComment || '');
    return value.trim() !== existingText.trim();
  });

  // Utkast som sparats i DB men aldrig skickats.
  //
  // ⚠️ Läs per-rad-flaggan i tråden, inte val.draft. Parent-flaggan är legacy
  // och sätts aldrig av backend (delete baseFields.draft) — den här filtreringen
  // returnerade därför alltid tomt, och efter en omladdning fanns ingenting kvar
  // som avslöjade att texten var oskickad.
  const draftComments = Object.entries(existingComments).filter(([key, val]) => {
    if (key === '__meetingRequest__') return false;
    if (!val || typeof val !== 'object') return false;
    if (!Array.isArray(val.thread) || val.thread.length === 0) return false;
    const kundrader = val.thread.filter(m => m?.by === 'customer' && m?.text);
    if (kundrader.length === 0) return false;
    // Ingen publicerad kundrad → hela kommentaren är oskickad
    return !kundrader.some(m => m.draft !== true);
  });

  const newComments = localNewComments.length > 0 ? localNewComments : draftComments;

  // Visa så fort det finns något oskickat.
  //
  // Kravet på hasAnyFeedbackOnEstimat gjorde att bannern bara syntes för kunder
  // som redan skickat feedback minst en gång. En förstagångskommentar fick
  // alltså ingen uppmaning alls — vilket är exakt hur 19 kommentarer från fem
  // kunder blev liggande oskickade mellan juni och augusti.
  if (newComments.length === 0 || !isCustomerView) {
    return null;
  }

  // Amber, inte grönt. Grönt läser som "klart" — men ingenting är skickat än,
  // och det var precis missförståndet som gjorde att kommentarer blev liggande.
  //
  // Klistrad högst upp: estimaten är långa och kunden står oftast längst ner när
  // hen är klar. En banner som bara syns i toppen hann aldrig läsas — den var
  // utanför skärmen i det ögonblick den behövdes.
  return (
    <div className="sticky top-0 z-30 mb-6 p-4 bg-amber-50 border border-amber-300 rounded-lg shadow-md">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          <div>
            <h3 className="text-lg font-semibold text-amber-900">
              {newComments.length} {newComments.length === 1 ? 'kommentar' : 'kommentarer'} har inte skickats
            </h3>
            <p className="text-sm text-amber-800">
              Dina kommentarer sparas medan du skriver, men din byggkonsult ser dem
              inte förrän du skickar dem.
            </p>
          </div>
        </div>
        <button
          onClick={onSendFeedback}
          disabled={submitting}
          className="flex-shrink-0 px-6 py-3 bg-leadme-pink hover:bg-leadme-pink-dark disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-md transition-colors"
        >
          {submitting
            ? 'Skickar...'
            : `💬 ${hasAnyFeedbackOnEstimat ? 'Skicka ny feedback' : 'Skicka feedback'} (${newComments.length})`}
        </button>
      </div>
    </div>
  );
};

export default NewFeedbackBanner;

